import { FormCheckbox } from '@ipfabric/ui-components/FormCheckbox/FormCheckbox';
import { FormMultiSelect } from '@ipfabric/ui-components/FormMultiSelect/FormMultiSelect';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import { type SourceOfTasks, sourceOfTasks } from '@ipfabric/utils';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import type { DiscoverySettingsObject } from '../../model/DiscoverySettingsObject';

const sourceOfTasksOptions = {
  aciEndpoint: 'ACI endpoints',
  xdp: 'xDP (neighbors)',
  arp: 'ARP',
  routes: 'Routing table',
  trace: 'Trace',
  ndpv6: 'NDPv6',
} satisfies Record<SourceOfTasks, string>;

const SOURCE_OF_TASKS_OPTIONS = Object.entries(sourceOfTasksOptions).map(
  ([value, label]) => ({
    value: value as SourceOfTasks,
    label,
  }),
);

export const limitDiscoveryTasks: FormSnippet<
  DiscoverySettingsObject,
  { sourceOfTasks: SourceOfTasks[]; alreadyDiscovered: boolean },
  { sourceOfTasks: SourceOfTasks[]; alreadyDiscovered: boolean },
  { alreadyDiscoveredValue: boolean }
> = {
  render: ({ form, hook }) => {
    return (
      <div className='flex flex-col gap-xs'>
        <FormMultiSelect
          control={form.control}
          name='sourceOfTasks'
          label='Neighbors identification'
          options={SOURCE_OF_TASKS_OPTIONS} // Placeholder for actual options
          description='Select the sources from which discovery tasks can be created.'
          disabled={hook.alreadyDiscoveredValue}
        />
        <FormCheckbox
          control={form.control}
          name='alreadyDiscovered'
          description='Limit discovery to already discovered devices'
        />
      </div>
    );
  },
  use: ({ form }) => {
    const alreadyDiscoveredValue = useWatch({
      name: 'alreadyDiscovered',
      control: form.control,
    });
    return { alreadyDiscoveredValue };
  },
  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.alreadyDiscovered =
      model.limitDiscoveryTasks?.alreadyDiscovered ?? false;
    fieldValuesDraft.sourceOfTasks =
      model.limitDiscoveryTasks?.sourceOfTasks ?? [];
  },
  toModel: (transformedValues, modelDraft) => {
    modelDraft.limitDiscoveryTasks = {
      ...modelDraft.limitDiscoveryTasks,
      alreadyDiscovered: transformedValues.alreadyDiscovered,
      sourceOfTasks: transformedValues.sourceOfTasks,
    };
  },
  schema: z.object({
    alreadyDiscovered: z.boolean(),
    sourceOfTasks: z
      .array(z.enum(sourceOfTasks))
      .refine((arr) => new Set(arr).size === arr.length, {
        message: 'Must be unique',
      }),
  }),
};
